import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, useTheme, alpha } from '@mui/material';
import {
  ArrowForward as ArrowIcon, ChevronRight as ChevronIcon,
  EventBusy as OverdueIcon, RateReviewOutlined as ReviewIcon, PauseCircleOutline as IdleIcon, FactCheckOutlined as ApproveIcon,
  GroupsOutlined as TeamsIcon, FolderOpen as ProjectIcon,
  FlagOutlined as FlagIcon, DonutLargeOutlined as DonutIcon, BoltOutlined as ActivityIcon, WorkOutline as ProjectsIcon,
} from '@mui/icons-material';
import { HBars, MiniHeat, Ring, Donut, Meter } from './Charts';
import { SoftHero, SoftCard, SoftTitle, Pill, EmptyState } from './Soft';
import { captionSx, fmtHours, fmtInt, fmtCompact, dayLabel, relativeTime, COLORS, periodWord, initials, tint } from './dashboardUtils';

const severityFill = (pct) => (pct >= 80 ? 'mint' : pct >= 50 ? 'lemon' : 'rose');
const AVATAR_FILLS = ['sky', 'mint', 'lavender', 'peach', 'lemon'];

/** One tinted tile in the "Needs attention" stack. */
const AttentionTile = ({ title, count, items, render, onOpen, icon: Icon, fill }) => {
  const theme = useTheme();
  const live = count > 0;
  const t = tint(theme, live ? fill : 'slate');
  return (
    <Box
      onClick={live ? onOpen : undefined}
      sx={{
        display: 'grid', gridTemplateColumns: '44px 1fr auto', gap: 3, alignItems: 'center', px: 4, py: 3.5, borderRadius: 4,
        bgcolor: t.bg, cursor: live ? 'pointer' : 'default', opacity: live ? 1 : 0.7,
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': live ? { transform: 'translateY(-1px)', boxShadow: '0 8px 20px rgba(16,24,40,0.08)' } : {},
      }}
    >
      <Box sx={{ width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#fff', color: t.ink }}>
        <Icon sx={{ fontSize: 22 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.2 }}>
          <Box component="span" sx={{ fontSize: '1.375rem', fontWeight: 800, letterSpacing: '-0.02em', mr: 1.5 }}>{fmtInt(count)}</Box>{title}
        </Typography>
        <Typography noWrap sx={{ fontSize: '0.75rem', color: t.ink, mt: 0.5, opacity: 0.9 }}>
          {live ? render(items[0]) : 'Nothing here'}
        </Typography>
      </Box>
      {live && <ChevronIcon sx={{ color: t.ink }} />}
    </Box>
  );
};

const STATUS_SLICES = [
  { key: 'todo', label: 'To do', fill: 'slate' },
  { key: 'inProgress', label: 'In progress', fill: 'sky' },
  { key: 'review', label: 'In review', fill: 'lemon' },
  { key: 'done', label: 'Done this period', fill: 'mint' },
];

/**
 * Manager view, soft style. Teams grid first (admins), then projects and the
 * activity stream, with what needs attention, task status and hours by
 * project in the rail. Tapping a team focuses the project-level widgets on it.
 */
const TeamOverview = ({ overview, header, user, onOpenReport, navigate, personal, reports }) => {
  const theme = useTheme();
  const { team, today, period, workingDays } = overview;
  const multiService = team.byService.length > 1;
  const word = periodWord(period);
  const s = {
    ...team.summary,
    daysDue: team.summary.daysDue ?? Math.max(0, ...team.members.map((m) => m.daysDue || 0)),
  };
  const nothingDue = s.daysDue === 0;
  const tasksByStatus = team.tasksByStatus || null;

  const [serviceId, setServiceId] = useState(null);
  const [showAllProjects, setShowAllProjects] = useState(false);

  const services = useMemo(() => {
    const map = new Map();
    team.members.forEach((m) => {
      const key = m.serviceId ?? 0;
      if (!map.has(key)) {
        map.set(key, { key, name: m.serviceName || 'No service', people: 0, upToDate: 0, missingDays: 0, hours: 0, flags: 0, loggedToday: 0, cells: workingDays.map((date) => ({ date, logged: 0, due: 0 })) });
      }
      const g = map.get(key);
      g.people += 1; g.missingDays += m.missingDays; g.hours += m.hours; g.flags += m.flags;
      if (m.daysDue > 0 && m.missingDays === 0) g.upToDate += 1;
      if (m.loggedToday > 0) g.loggedToday += 1;
      m.days.forEach((d, i) => {
        if (!g.cells[i]) return;
        if (d.status === 'logged' || d.status === 'over') { g.cells[i].logged += 1; g.cells[i].due += 1; }
        else if (d.status === 'missing') g.cells[i].due += 1;
      });
    });
    return [...map.values()].map((g) => ({ ...g, pct: g.people ? Math.round((g.upToDate / g.people) * 100) : 0 })).sort((a, b) => b.people - a.people);
  }, [team.members, workingDays]);

  // The team focus narrows the project-level widgets.
  const inService = (sid) => serviceId == null || (sid ?? 0) === serviceId;
  const projects = (team.projects || []).filter((p) => inService(p.serviceId));
  const activity = (team.activity || []).filter((a) => inService(a.serviceId) || inService(a.userServiceId));
  const hoursByProject = team.hoursByProject.filter((p) => inService(p.serviceId));
  const focusName = serviceId != null ? services.find((x) => x.key === serviceId)?.name : null;
  const visibleProjects = showAllProjects ? projects : projects.slice(0, 8);
  const billable = hoursByProject.filter((p) => p.isBillable !== false).reduce((t, p) => t + p.hours, 0);
  const nonBillable = hoursByProject.filter((p) => p.isBillable === false).reduce((t, p) => t + p.hours, 0);
  const canReview = ['admin', 'QC', 'cmo'].includes(user?.role);
  const slices = tasksByStatus ? STATUS_SLICES.map((sl) => ({ ...sl, value: tasksByStatus[sl.key] || 0, color: tint(theme, sl.fill).ink })) : [];
  const focusSuffix = focusName ? ` · ${focusName}` : '';
  // With fewer than four teams a full-width Teams card is mostly empty, so the
  // teams become a compact list in the right column above Needs attention.
  const compactTeams = multiService && services.length < 4;

  // One team as a single compact row: ring, name + people, week strip, missing
  // days. The same row is used in the full-width grid (admins, many teams) and
  // the side list (a few teams), so ten teams fit in a third of the old height.
  const teamTile = (sv) => {
    const fill = nothingDue ? 'sky' : severityFill(sv.pct);
    const t = tint(theme, fill);
    const active = serviceId === sv.key;
    return (
      <Box
        key={sv.key}
        onClick={() => setServiceId(active ? null : sv.key)}
        sx={{
          p: 2.5, borderRadius: 3, cursor: 'pointer', bgcolor: t.bg, minWidth: 0,
          display: 'flex', alignItems: 'center', gap: 2.5,
          border: `2px solid ${active ? t.ink : 'transparent'}`,
          opacity: serviceId != null && !active ? 0.55 : 1,
          transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 24px rgba(16,24,40,0.08)' },
        }}
      >
        <Ring value={(nothingDue ? (sv.people ? sv.loggedToday / sv.people : 0) : sv.pct / 100)} color={t.ink} size={44} stroke={5} track={alpha(t.ink, 0.18)}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: t.ink }}>
            {nothingDue ? fmtInt(sv.loggedToday) : `${sv.pct}%`}
          </Typography>
        </Ring>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 700 }}>{sv.name}</Typography>
          <Typography noWrap sx={{ fontSize: '0.75rem', color: t.ink, fontWeight: 600 }}>
            {fmtInt(sv.people)} people{nothingDue ? ' · logged today' : ` · ${fmtInt(sv.upToDate)} up to date`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <MiniHeat cells={sv.cells} today={today} />
          <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 700, color: t.ink }}>
            {sv.missingDays > 0 ? `${fmtInt(sv.missingDays)} missing days` : `${fmtCompact(sv.hours)} h`}
          </Typography>
        </Box>
      </Box>
    );
  };

  const teamsTitle = (
    <SoftTitle
      icon={TeamsIcon} fill="sky" title="Teams" sub="Colour is how the team is doing. Tap one to focus the page on it."
      right={serviceId != null && <Button size="small" onClick={() => setServiceId(null)} sx={{ fontWeight: 700, borderRadius: 999 }}>All teams</Button>}
    />
  );

  const attentionCard = (sx) => (
    <SoftCard sx={sx}>
      <SoftTitle icon={FlagIcon} fill="peach" title="Needs attention" />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {team.attention.timesheets && (
          <AttentionTile
            title={`timesheet${team.attention.timesheets.count === 1 ? '' : 's'} to approve`} count={team.attention.timesheets.count} items={team.attention.timesheets.items}
            icon={ApproveIcon} fill="mint"
            render={(s) => `${s.userName} · ${s.periodType === 'month' ? dayLabel(s.periodStart, { month: 'long' }) : `week of ${dayLabel(s.periodStart, { day: 'numeric', month: 'short' })}`} · ${fmtHours(s.totalHours)} h`}
            onOpen={() => navigate('/approvals')}
          />
        )}
        <AttentionTile
          title="overdue tasks" count={team.attention.overdueTasks.count} items={team.attention.overdueTasks.items}
          icon={OverdueIcon} fill="rose"
          render={(t) => `${t.title} · due ${dayLabel(t.deadline, { day: 'numeric', month: 'short' })}`}
          onOpen={() => navigate('/tasks')}
        />
        <AttentionTile
          title="waiting for review" count={team.attention.awaitingReview.count} items={team.attention.awaitingReview.items}
          icon={ReviewIcon} fill="lemon"
          render={(t) => `${t.title}${t.submitted ? ` · since ${dayLabel(t.submitted, { day: 'numeric', month: 'short' })}` : ''}`}
          onOpen={() => navigate(canReview ? '/qc-review' : '/tasks')}
        />
        <AttentionTile
          title={`projects idle ${team.attention.idleProjects.days}+ days`} count={team.attention.idleProjects.count} items={team.attention.idleProjects.items}
          icon={IdleIcon} fill="sky"
          render={(p) => `${p.projectKey || p.name} · last ${p.lastLogged ? dayLabel(p.lastLogged, { day: 'numeric', month: 'short' }) : 'never'}`}
          onOpen={() => navigate('/projects')}
        />
      </Box>
    </SoftCard>
  );

  const statusCard = (sx) => tasksByStatus && (
    <SoftCard sx={sx}>
      <SoftTitle icon={DonutIcon} fill="sky" title="Task status" sub="Open tasks on active projects" />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <Donut slices={slices} size={140} thickness={18} centerLabel="tasks" />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
          {slices.map((sl) => (
            <Box key={sl.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: sl.color, flexShrink: 0 }} />
              <Typography noWrap sx={{ fontSize: '0.8125rem', flex: 1 }}>{sl.label}</Typography>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtInt(sl.value)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </SoftCard>
  );

  const hoursCard = (sx) => (
    <SoftCard sx={sx}>
      <SoftTitle
        icon={ProjectIcon} fill="mint" title={`Hours by project${focusSuffix}`} sub={`${fmtCompact(billable)} h billable · ${fmtCompact(nonBillable)} h non-billable`}
        right={<Button size="small" endIcon={<ArrowIcon />} onClick={() => onOpenReport('projectHours')} sx={{ fontWeight: 700, borderRadius: 999 }}>Report</Button>}
      />
      {hoursByProject.length === 0 ? (
        <EmptyState icon={ProjectIcon} fill="mint" title={`No hours logged ${word} yet`} sub="This fills in as the team logs time. Try Last week to see the previous period." />
      ) : (
        <HBars
          rows={hoursByProject.slice(0, 8)}
          labelOf={(r) => r.projectKey || r.name}
          subOf={(r) => `${r.people} ${r.people === 1 ? 'person' : 'people'}${r.isBillable === false ? ' · non-billable' : ''}`}
          valueOf={(r) => r.hours}
          valueLabel={(r) => `${fmtHours(r.hours)} h`}
          colorOf={(r) => (r.isBillable === false ? COLORS.muted : theme.palette.primary.main)}
          labelWidth="42%"
        />
      )}
    </SoftCard>
  );

  const colSx = { gridColumn: { lg: 'span 5' }, display: 'flex', flexDirection: 'column', gap: 4 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <SoftHero title={header?.title} subtitle={header?.subtitle} actions={header?.actions} />

      {personal}

      {/* Widget grid: 12 columns, rows share their height so nothing leaves a hole. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(12, minmax(0, 1fr))' }, gap: 4, alignItems: 'stretch', '& > *': { minWidth: 0 } }}>
        {/* Teams, full width, for admins / CMOs who span four or more services */}
        {multiService && !compactTeams && (
          <Box sx={{ gridColumn: { lg: 'span 12' } }}>
            <SoftCard>
              {teamsTitle}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 2.5 }}>
                {services.map(teamTile)}
              </Box>
            </SoftCard>
          </Box>
        )}

        {/* Latest activity beside what needs attention; the two are about the same height. */}
        <Box sx={{ gridColumn: { lg: 'span 7' } }}>
          <SoftCard sx={{ height: '100%' }}>
            <SoftTitle icon={ActivityIcon} fill="lemon" title={`Latest activity${focusSuffix}`} sub="Newest time entries from your team" />
            {activity.length === 0 ? <EmptyState icon={ActivityIcon} fill="lemon" title="Nothing logged yet" sub="New time entries from your team show up here as they come in." /> : activity.slice(0, 8).map((a, i) => {
              const av = tint(theme, AVATAR_FILLS[a.userId % AVATAR_FILLS.length]);
              return (
                <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 3, py: 2.25, borderBottom: i === Math.min(activity.length, 8) - 1 ? 'none' : `1px solid ${theme.palette.divider}` }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: av.ink, bgcolor: av.bg }}>
                    {initials(a.userName)}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontSize: '0.875rem' }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>{a.userName}</Box> logged <Box component="span" sx={{ fontWeight: 700 }}>{fmtHours(a.hours)} h</Box> on {a.projectKey || a.projectName}
                    </Typography>
                    <Typography noWrap sx={{ ...captionSx, fontSize: '0.75rem' }}>{a.taskTitle} · for {dayLabel(a.date, { weekday: 'short', day: 'numeric', month: 'short' })}</Typography>
                  </Box>
                  <Typography sx={{ ...captionSx, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{relativeTime(a.createdAt)}</Typography>
                </Box>
              );
            })}
          </SoftCard>
        </Box>

        {/* Right column beside Latest activity */}
        <Box sx={colSx}>
          {compactTeams && (
            <SoftCard>
              {teamsTitle}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {services.map(teamTile)}
              </Box>
            </SoftCard>
          )}
          {attentionCard({ flex: 1 })}
        </Box>

        {/* Projects | Task status + Hours by project: the two short cards together match the project list. */}
        <Box sx={{ gridColumn: { lg: 'span 7' } }}>
          <SoftCard sx={{ height: '100%' }}>
            <SoftTitle
              icon={ProjectsIcon} fill="lavender" title={`Projects${focusSuffix}`}
              sub={projects.length ? `${fmtInt(projects.length)} active · busiest ${word} first` : 'No active projects here'}
              right={<Button size="small" endIcon={<ArrowIcon />} onClick={() => navigate('/projects')} sx={{ fontWeight: 700, borderRadius: 999 }}>All projects</Button>}
            />
            {visibleProjects.map((p, i) => {
              const done = p.totalTasks ? p.doneTasks / p.totalTasks : 0;
              const idle = !p.lastLogged || p.lastLogged < workingDays[0];
              const lav = tint(theme, 'lavender');
              return (
                <Box key={p.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.5fr) minmax(140px, 1fr) auto' }, gap: 3, alignItems: 'center', py: 3, borderBottom: i === visibleProjects.length - 1 ? 'none' : `1px solid ${theme.palette.divider}` }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{p.projectKey || p.name}</Typography>
                    <Typography noWrap sx={{ ...captionSx, fontSize: '0.75rem' }}>
                      {p.projectKey ? `${p.name} · ` : ''}{p.manager ? `${p.manager} · ` : ''}{fmtInt(p.people)} people
                    </Typography>
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary' }}>{fmtInt(p.doneTasks)} of {fmtInt(p.totalTasks)} tasks done</Typography>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{Math.round(done * 100)}%</Typography>
                    </Box>
                    <Meter value={done} color={lav.ink} height={8} track={lav.bg} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
                    <Pill fill={p.hours > 0 ? 'sky' : 'slate'} dot={false}>{fmtHours(p.hours)} h {word}</Pill>
                    {p.overdueTasks > 0 && <Pill fill="rose" dot={false}>{fmtInt(p.overdueTasks)} overdue</Pill>}
                    {idle && <Pill fill="peach" dot={false}>idle since {p.lastLogged ? dayLabel(p.lastLogged, { day: 'numeric', month: 'short' }) : 'ever'}</Pill>}
                  </Box>
                </Box>
              );
            })}
            {projects.length === 0 && <EmptyState icon={ProjectsIcon} fill="lavender" title="No active projects" sub={focusName ? `${focusName} has no active projects right now.` : 'Projects you manage or belong to appear here.'} />}
            {projects.length > 8 && (
              <Box sx={{ pt: 3, textAlign: 'center' }}>
                <Button size="small" onClick={() => setShowAllProjects((v) => !v)} sx={{ fontWeight: 700, borderRadius: 999 }}>
                  {showAllProjects ? 'Show fewer' : `Show all ${fmtInt(projects.length)}`}
                </Button>
              </Box>
            )}
          </SoftCard>
        </Box>

        <Box sx={colSx}>
          {statusCard()}
          {hoursCard({ flex: 1 })}
        </Box>

        {/* Row: reports, full width */}
        <Box sx={{ gridColumn: { lg: 'span 12' } }}>{reports}</Box>
      </Box>
    </Box>
  );
};

export default TeamOverview;
